import { clsx } from "clsx";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, ImageOff, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { PostMedia } from "@/features/feed/types";

interface MediaViewerProps {
	media: PostMedia[];
	initialIndex: number;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function MediaViewer({
	media,
	initialIndex,
	open,
	onOpenChange,
}: MediaViewerProps) {
	const [emblaRef, emblaApi] = useEmblaCarousel({
		startIndex: initialIndex,
		loop: false,
	});
	const [selected, setSelected] = useState(initialIndex);
	const [canScrollPrev, setCanScrollPrev] = useState(false);
	const [canScrollNext, setCanScrollNext] = useState(false);
	const [failedIds, setFailedIds] = useState<string[]>([]);

	useEffect(() => {
		if (!emblaApi) return;

		const syncSelection = () => {
			const index = emblaApi.selectedScrollSnap();
			setSelected(index);
			setCanScrollPrev(emblaApi.canScrollPrev());
			setCanScrollNext(emblaApi.canScrollNext());
			emblaApi.slideNodes().forEach((slide, slideIndex) => {
				if (slideIndex === index) return;
				slide.querySelector("video")?.pause();
			});
		};

		emblaApi.on("select", syncSelection);
		emblaApi.on("reInit", syncSelection);
		syncSelection();

		return () => {
			emblaApi.off("select", syncSelection);
			emblaApi.off("reInit", syncSelection);
		};
	}, [emblaApi]);

	useEffect(() => {
		setSelected(initialIndex);
		emblaApi?.scrollTo(initialIndex, true);
	}, [emblaApi, initialIndex]);

	useEffect(() => {
		if (!open) setFailedIds([]);
	}, [open]);

	const markFailed = useCallback((id: string) => {
		setFailedIds((current) =>
			current.includes(id) ? current : [...current, id],
		);
	}, []);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === "ArrowLeft") {
				event.preventDefault();
				emblaApi?.scrollPrev();
			}
			if (event.key === "ArrowRight") {
				event.preventDefault();
				emblaApi?.scrollNext();
			}
		},
		[emblaApi],
	);

	const handleSlideClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (event.target === event.currentTarget) onOpenChange(false);
		},
		[onOpenChange],
	);

	if (media.length === 0) return null;

	const caption = media[selected]?.caption;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className="media-viewer"
				showCloseButton={false}
				onKeyDown={handleKeyDown}
			>
				<DialogTitle className="visually-hidden">
					Visualizador de mídia
				</DialogTitle>

				<div className="media-viewer__viewport" ref={emblaRef}>
					<div className="media-viewer__track">
						{media.map((item) => {
							const isError = failedIds.includes(item.id);
							return (
								<div
									key={item.id}
									className={clsx("media-viewer__slide", isError && "is-error")}
									onClick={handleSlideClick}
								>
									{item.type === "video" ? (
										// biome-ignore lint/a11y/useMediaCaption: user-uploaded video has no caption track
										<video
											controls
											preload="metadata"
											playsInline
											poster={item.thumbnailUrl}
											className="media-viewer__video"
										>
											<source src={item.url} />
										</video>
									) : isError ? (
										<>
											<ImageOff />
											<span>Não foi possível carregar a imagem</span>
										</>
									) : (
										<img
											src={item.url}
											alt={item.caption ?? "Mídia do relato"}
											className="media-viewer__img"
											onError={() => markFailed(item.id)}
										/>
									)}
								</div>
							);
						})}
					</div>
				</div>

				<span className="media-viewer__counter">
					{selected + 1} / {media.length}
				</span>

				{media.length > 1 && (
					<>
						<button
							type="button"
							className="media-viewer__nav"
							data-direction="prev"
							aria-label="Mídia anterior"
							disabled={!canScrollPrev}
							onClick={() => emblaApi?.scrollPrev()}
						>
							<ChevronLeft />
						</button>
						<button
							type="button"
							className="media-viewer__nav"
							data-direction="next"
							aria-label="Próxima mídia"
							disabled={!canScrollNext}
							onClick={() => emblaApi?.scrollNext()}
						>
							<ChevronRight />
						</button>
					</>
				)}

				<button
					type="button"
					className="media-viewer__close"
					aria-label="Fechar visualizador"
					onClick={() => onOpenChange(false)}
				>
					<X />
				</button>

				{caption && <p className="media-viewer__caption">{caption}</p>}
			</DialogContent>
		</Dialog>
	);
}
